import { useEffect, useState } from 'react';
import { Input } from '@heroui/react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/**
 * Debounced free-text filter input. Keeps keystrokes local for instant feedback
 * and only commits (`onCommit`) after the user pauses, so typing a long value
 * (e.g. a 64-char hash or a full URL) fires one query instead of one per key.
 * Shared by the attachment and URL investigation toolbars.
 */
export function DebouncedInput({ label, placeholder, value, onCommit, delay = 300, className = 'w-full' }: {
  label: string;
  placeholder: string;
  value: string;
  onCommit: (v: string) => void;
  delay?: number;
  className?: string;
}) {
  const [draft, setDraft] = useState(value);
  const debounced = useDebouncedValue(draft, delay);
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (debounced !== value) onCommit(debounced); }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Input aria-label={label} size="sm" variant="bordered" placeholder={placeholder} className={className}
      value={draft} onValueChange={setDraft} isClearable onClear={() => { setDraft(''); onCommit(''); }} />
  );
}
